import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, AlertTriangle, Shield, Globe, Search, Loader2, Sparkles, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AI_DISCLAIMER = "AI-generated estimates based on supplier name, transaction patterns, and publicly available knowledge. Not verified external data — should not replace due diligence for critical suppliers.";

const Tooltip = ({ text }) => (
  <div className="group relative inline-flex">
    <Info size={14} className="text-amber-500 cursor-help" />
    <div className="hidden group-hover:block absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-slate-900 text-white text-xs rounded-sm shadow-lg leading-relaxed">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
    </div>
  </div>
);

const RiskBadge = ({ score }) => {
  if (score >= 7) return <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-red-50 text-red-700">High ({score})</span>;
  if (score >= 4) return <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-700">Medium ({score})</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-50 text-green-700">Low ({score})</span>;
};

const SizeBadge = ({ size }) => {
  const styles = {
    'Enterprise': 'bg-purple-50 text-purple-700',
    'Mid-Market': 'bg-blue-50 text-blue-700',
    'SME': 'bg-teal-50 text-teal-700',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${styles[size] || 'bg-slate-100 text-slate-600'}`}>{size}</span>;
};

const ProfileCard = ({ profile }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 hover:shadow-sm transition-shadow" data-testid={`profile-card-${profile.supplier_name}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-bold text-[#0F172A] truncate">{profile.supplier_name}</h3>
            <Tooltip text={AI_DISCLAIMER} />
          </div>
          <p className="text-xs text-slate-500">{profile.industry_sector}{profile.sub_sector ? ` > ${profile.sub_sector}` : ''}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <RiskBadge score={profile.risk_score} />
          <SizeBadge size={profile.company_size} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
        <div className="bg-slate-50 rounded-sm p-2">
          <p className="text-slate-400 mb-0.5">Total Spend</p>
          <p className="font-semibold text-slate-800">{profile.currency || 'USD'} {(profile.total_spend || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-slate-50 rounded-sm p-2">
          <p className="text-slate-400 mb-0.5">Transactions</p>
          <p className="font-semibold text-slate-800">{profile.transaction_count}</p>
        </div>
        <div className="bg-slate-50 rounded-sm p-2 flex items-center gap-1.5">
          <Globe size={12} className="text-slate-400" />
          <div>
            <p className="text-slate-400 mb-0.5">HQ</p>
            <p className="font-semibold text-slate-800">{profile.hq_country || 'Unknown'}</p>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-600 mb-2 leading-relaxed">{profile.description}</p>

      {(profile.compliance_flags?.length > 0 || profile.risk_factors?.length > 0) && (
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2"
          data-testid={`expand-${profile.supplier_name}`}>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? 'Hide details' : `${(profile.compliance_flags?.length || 0) + (profile.risk_factors?.length || 0)} flags & factors`}
        </button>
      )}

      {expanded && (
        <div className="space-y-2 mt-2 pt-2 border-t border-slate-100">
          {profile.risk_factors?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Risk Factors</p>
              <div className="flex flex-wrap gap-1">
                {profile.risk_factors.map((f, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded-sm">{f}</span>
                ))}
              </div>
            </div>
          )}
          {profile.compliance_flags?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Compliance Flags</p>
              <div className="flex flex-wrap gap-1">
                {profile.compliance_flags.map((f, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-amber-50 text-amber-600 rounded-sm">{f}</span>
                ))}
              </div>
            </div>
          )}
          {profile.category_specialization?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Specialization</p>
              <div className="flex flex-wrap gap-1">
                {profile.category_specialization.map((c, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-sm">{c}</span>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-1 mt-1">
            <p className="text-[10px] text-slate-400">Confidence: {Math.round((profile.confidence || 0) * 100)}%</p>
            <Tooltip text={AI_DISCLAIMER} />
          </div>
        </div>
      )}
    </div>
  );
};

export const SupplierProfiles = ({ token }) => {
  const [profiles, setProfiles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [riskSummary, setRiskSummary] = useState(null);

  const fetchProfiles = useCallback(async () => {
    try {
      const [profRes, riskRes] = await Promise.all([
        axios.get(`${API}/enrichment/profiles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/enrichment/risk-summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProfiles(profRes.data.profiles || []);
      setRiskSummary(riskRes.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [token]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/spend/processing-sessions`, { headers: { Authorization: `Bearer ${token}` } });
      const classified = (res.data.sessions || []).filter(s => s.status === 'classified');
      setSessions(classified);
      if (classified.length > 0 && !selectedSession) setSelectedSession(classified[0].id);
    } catch { /* ignore */ }
  }, [token, selectedSession]);

  useEffect(() => { fetchProfiles(); fetchSessions(); }, [fetchProfiles, fetchSessions]);

  const handleEnrich = async () => {
    if (!selectedSession) { toast.error('Select a session first'); return; }
    setEnriching(true);
    try {
      const res = await axios.post(`${API}/enrichment/enrich/${selectedSession}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enrichment failed');
    } finally { setEnriching(false); }
  };

  const filtered = profiles.filter(p => {
    const matchSearch = !search || p.supplier_name.toLowerCase().includes(search.toLowerCase()) ||
      p.industry_sector?.toLowerCase().includes(search.toLowerCase());
    const matchRisk = riskFilter === 'all' ||
      (riskFilter === 'high' && p.risk_score >= 7) ||
      (riskFilter === 'medium' && p.risk_score >= 4 && p.risk_score < 7) ||
      (riskFilter === 'low' && p.risk_score < 4);
    return matchSearch && matchRisk;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 size={32} className="animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="supplier-profiles-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Supplier Profiles</h1>
            <Tooltip text={AI_DISCLAIMER} />
          </div>
          <p className="text-slate-600">AI-enriched supplier intelligence with risk scoring and industry analysis</p>
        </div>
      </div>

      {/* Disclaimer Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2" data-testid="ai-disclaimer-banner">
        <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          <span className="font-semibold">AI-Generated Profiles:</span> {AI_DISCLAIMER}
        </p>
      </div>

      {/* Enrich Controls */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-end gap-4" data-testid="enrich-controls">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Enrich from Session</label>
          <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)}
            data-testid="enrich-session-select"
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {sessions.length === 0 && <option value="">No classified sessions</option>}
            {sessions.map(s => (
              <option key={s.id} value={s.id}>
                {`${s.filename} — ${s.total_records} records${s.industry ? ` (${s.industry})` : ''}`}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={handleEnrich} disabled={enriching || !selectedSession}
          className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5 h-10" data-testid="enrich-button">
          {enriching ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {enriching ? 'Enriching...' : 'Enrich Suppliers'}
        </Button>
      </div>

      {/* Risk Summary */}
      {riskSummary && riskSummary.total > 0 && (
        <div className="grid grid-cols-4 gap-4" data-testid="risk-summary">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-2xl font-bold text-[#0F172A]">{riskSummary.total}</p>
            <p className="text-xs text-slate-500">Total Profiles</p>
          </div>
          <div className="bg-white border border-red-200 rounded-sm p-4 cursor-pointer hover:bg-red-50 transition-colors"
            onClick={() => setRiskFilter(riskFilter === 'high' ? 'all' : 'high')}>
            <p className="text-2xl font-bold text-red-700">{riskSummary.high_risk?.length || 0}</p>
            <p className="text-xs text-red-600 font-medium">High Risk (7+)</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-sm p-4 cursor-pointer hover:bg-amber-50 transition-colors"
            onClick={() => setRiskFilter(riskFilter === 'medium' ? 'all' : 'medium')}>
            <p className="text-2xl font-bold text-amber-700">{riskSummary.medium_risk || 0}</p>
            <p className="text-xs text-amber-600 font-medium">Medium Risk (4-6)</p>
          </div>
          <div className="bg-white border border-green-200 rounded-sm p-4 cursor-pointer hover:bg-green-50 transition-colors"
            onClick={() => setRiskFilter(riskFilter === 'low' ? 'all' : 'low')}>
            <p className="text-2xl font-bold text-green-700">{riskSummary.low_risk || 0}</p>
            <p className="text-xs text-green-600 font-medium">Low Risk (1-3)</p>
          </div>
        </div>
      )}

      {/* High Risk Alerts */}
      {riskSummary?.high_risk?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4" data-testid="high-risk-alerts">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} className="text-red-600" />
            <h3 className="text-sm font-semibold text-red-800">High Risk Suppliers</h3>
            <Tooltip text="High risk scores (7+) indicate potential supply chain vulnerabilities such as single-source dependency, high spend concentration, or limited alternatives. These are AI estimates — verify with your procurement team." />
          </div>
          <div className="space-y-1">
            {riskSummary.high_risk.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="font-medium text-red-800">{s.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-red-600">{s.currency || 'USD'} {(s.spend || 0).toLocaleString()}</span>
                  <span className="text-red-500 font-semibold">Risk: {s.score}/10</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filter */}
      {profiles.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search suppliers or industries..." data-testid="profile-search"
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {riskFilter !== 'all' && (
            <button onClick={() => setRiskFilter('all')}
              className="text-xs px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-sm text-slate-600" data-testid="clear-filter">
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* Profile Cards */}
      {profiles.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Building2 size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-700">No supplier profiles yet</p>
            <p className="text-sm text-slate-500 mt-1 mb-4">Select a classified session above and click "Enrich Suppliers" to generate AI-powered profiles.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="profiles-grid">
          {filtered.map(p => <ProfileCard key={p.supplier_name} profile={p} />)}
          {filtered.length === 0 && (
            <div className="col-span-2 text-center py-10 text-sm text-slate-500">No profiles match your search</div>
          )}
        </div>
      )}
    </div>
  );
};
